/**
 * collection-load-more
 *
 * Expects global helpers:
 *   - AuroraProduct.init()
 *   - facetsExecuteScripts(container)
 *   - FacetFiltersForm.hideLoadingSpinners()
 *
 * Relies on DOM structure:
 *   - #product-grid exists inside ProductGridContainer
 *   - collection-load-more element exposes data-section-id, data-next-url, data-prev-url
 */

if (!customElements.get("collection-load-more")) {
  class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      // Initialize component state, attach listeners, and ready pagination/infinite-scroll behaviours.
      if (!this.initializeInstanceState()) return;
      if (this.paginationType !== "numbered") {
        this.initializeLoadMore();
        return;
      }

      void this.appendOOSProductsIfLastPage();
    }

    async appendOOSProducts() {
      const oosUrl = this.buildOosRequestUrl();
      console.log("oosURL", oosUrl);
      if (!oosUrl) return false;

      const oosHtml = await this.fetchPageDocument(oosUrl);
      const didAppend = this.appendOOSProductsFromDocument(oosHtml);
      if (didAppend) {
        this.runPostFetchInitializers();
      }
      return didAppend;
    }

    async appendOOSProductsIfLastPage() {
      if (!this.shouldAppendOOSProducts()) {
        this.updateCollectionEmptyStateIfAvailable();
        return false;
      }

      this.isAppendingOOS = true;
      try {
        const didAppend = await this.appendOOSProducts();
        this.oosAppended = didAppend;
        return didAppend;
      } catch (error) {
        console.error("Error loading out-of-stock products:", error);
        return false;
      } finally {
        this.isAppendingOOS = false;
        this.updateCollectionEmptyStateIfAvailable();
      }
    }

    shouldAppendOOSProducts() {
      // Out-of-stock products are now rendered inline server-side by the collection grid
      // (interleaved on standard sorts, bottom on the custom sorts). Appending them here too
      // would duplicate them, so this client-side append is disabled.
      return false;
    }

    hasAvailabilityFilter() {
      const params = this.getCurrentSearchParams();
      const availabilityValue = params.get("filter.v.availability");
      return availabilityValue === "0" || availabilityValue === "1";
    }

    isLastPage() {
      const currentPage = CollectionLoadMore.parsePageNumber(this.dataset.currentPage);
      const totalPages = CollectionLoadMore.parsePageNumber(this.dataset.totalPages);
      if (currentPage != null && totalPages != null) {
        return currentPage >= totalPages;
      }

      return !this.hasNextPage();
    }

    buildOosRequestUrl() {
      const baseUrl = this.dataset.path || window.location.pathname;
      const url = this.buildPageRequestUrl(baseUrl);
      if (!url) return null;
      url.searchParams.set("filter.v.availability", "0");
      return url;
    }

    appendOOSProductsFromDocument(html) {
      if (html.querySelector(".aur-coll-not-found-wrapper")) return false;

      const oosGrid = html.getElementById(CollectionLoadMore.ids.productGrid);
      const currentGrid = document.getElementById(CollectionLoadMore.ids.productGrid);
      if (!oosGrid || !currentGrid) return false;

      const nodes = Array.from(oosGrid.querySelectorAll(":scope > *"));
      if (!nodes.length) return false;

      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      currentGrid.appendChild(fragment);
      return true;
    }

    updateCollectionEmptyStateIfAvailable() {
      if (typeof updateCollectionEmptyState === "function") {
        updateCollectionEmptyState();
      }
    }

    disconnectedCallback() {
      if (this.loadMoreButton && this.onLoadMoreButtonClick) {
        this.loadMoreButton.removeEventListener("click", this.onLoadMoreButtonClick);
      }

      if (this.prevButton && this.onPrevClick) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }

      this.teardownInfiniteScroll();
    }

    initializeLoadMore() {
      this.infiniteScrollPaused = CollectionLoadMore.shouldPauseInfiniteScrollOnConnect();
      this.onPrevClick = this.handlePreviousClick.bind(this);
      this.ensurePreviousButtonExists();
      this.bindEventListeners();
      this.updateLoadMoreButtonState();
      this.updatePreviousButtonState();
      this.setupInfiniteScroll();
      this.updateInfiniteScrollObserver();
      this.appendOOSProductsIfLastPage();
      CollectionLoadMore.registerGlobalEventsOnce();
    }

    initializeInstanceState() {
      this.sectionId = this.dataset.sectionId;
      this.loadMoreButton = this.querySelector(CollectionLoadMore.selectors.loadMoreButton);
      // if (!this.loadMoreButton || !this.sectionId) return false;

      this.spinner = this.querySelector(CollectionLoadMore.selectors.spinner);
      this.loadingText = this.dataset.loadingText || "Loading…";
      this.finishedText = this.dataset.finishedText || "";
      const fallbackButtonText = this.loadMoreButton ? this.loadMoreButton.textContent.trim() : "";
      this.loadMoreButtonText = this.dataset.buttonText || fallbackButtonText;
      this.setAttribute("data-button-text", this.loadMoreButtonText);
      this.previousButtonText = this.dataset.previousButtonText || "Load Previous";
      this.setAttribute("data-previous-button-text", this.previousButtonText);

      const initialPage = CollectionLoadMore.parsePageNumber(this.dataset.currentPage) || CollectionLoadMore.getCurrentPageNumber();
      this.lowestLoadedPage = initialPage;
      this.highestLoadedPage = initialPage;
      this.autoLoadPreviousEnabled = false;
      this.prependReferenceTop = null;
      this.paginationType = this.dataset.paginationType;
      this.oosAppended = false;
      this.isAppendingOOS = false;
      this.lastOosQuery = this.getCurrentQueryString();
      return true;
    }

    bindEventListeners() {
      this.onLoadMoreButtonClick = this.loadNextPage.bind(this);
      this.loadMoreButton.addEventListener("click", this.onLoadMoreButtonClick);
    }

    updateLoadMoreButtonState() {
      const hasNext = this.hasNextPage();
      this.loadMoreButton.hidden = !hasNext && !this.finishedText;
      this.loadMoreButton.disabled = !hasNext;
      this.loadMoreButton.textContent = hasNext ? this.loadMoreButtonText : this.finishedText || this.loadMoreButtonText;
      this.updateInfiniteScrollObserver();
    }

    /**
     * Fetches and appends the next collection page while keeping URL/history in sync.
     */
    async loadNextPage() {
      if (!this.hasNextPage() || this.isLoading) return;
      this.isLoading = true;
      this.loadMoreButton.disabled = true;
      this.spinner?.classList.remove("hidden");
      this.loadMoreButton.textContent = this.loadingText;

      const nextUrl = this.buildPageRequestUrl(this.dataset.nextUrl);

      try {
        const html = await this.fetchPageDocument(nextUrl);
        this.appendNextPageProducts(html);
        this.syncPaginationFromResponse(html, "next");
        this.syncBrowserUrl(nextUrl);
        const didAppendOOS = await this.appendOOSProductsIfLastPage();
        if (!didAppendOOS) {
          this.runPostFetchInitializers();
        }
        // document.dispatchEvent(new CustomEvent("collection:page-appended", { detail: { page: parseInt(nextUrl.searchParams.get("page"), 10) } }));
      } catch (error) {
        console.error("Error loading next page:", error);
      } finally {
        this.isLoading = false;
        this.spinner?.classList.add("hidden");
        this.updateLoadMoreButtonState();
        this.updatePreviousButtonState();
      }
    }

    /**
     * Fetches and prepends the previous collection page, preserving the viewport anchor.
     */
    async loadPreviousPage() {
      if (!this.hasPreviousPage() || this.isLoading) return;

      this.isLoading = true;
      if (this.prevButton) {
        this.prevButton.disabled = true;
        this.prevButton.textContent = this.loadingText;
      }
      this.loadMoreButton.disabled = true;
      this.spinner?.classList.remove("hidden");

      const productGrid = document.getElementById(CollectionLoadMore.ids.productGrid);
      this.prependReferenceElement = productGrid?.firstElementChild || null;
      this.prependReferenceTop = this.prependReferenceElement ? this.prependReferenceElement.getBoundingClientRect().top : null;

      const prevUrl = this.buildPageRequestUrl(this.dataset.prevUrl);

      try {
        const html = await this.fetchPageDocument(prevUrl);
        this.prependPreviousPageProducts(html);
        this.syncPaginationFromResponse(html, "previous");
        this.syncBrowserUrl(prevUrl);
        this.runPostFetchInitializers();
      } catch (error) {
        console.error("Error loading previous page:", error);
        this.prependReferenceElement = null;
        this.prependReferenceTop = null;
      } finally {
        this.isLoading = false;
        if (this.prevButton) {
          this.prevButton.textContent = this.previousButtonText;
        }
        this.spinner?.classList.add("hidden");
        this.updateLoadMoreButtonState();
        this.updatePreviousButtonState();
      }
    }

    handlePreviousClick(event) {
      event.preventDefault();
      this.loadPreviousPage();
    }

    appendNextPageProducts(html) {
      const nextGrid = html.getElementById(CollectionLoadMore.ids.productGrid);
      const currentGrid = document.getElementById(CollectionLoadMore.ids.productGrid);
      if (!nextGrid || !currentGrid) return;

      const nodes = Array.from(nextGrid.querySelectorAll(":scope > *"));
      if (!nodes.length) return;

      nodes.forEach((node) => currentGrid.appendChild(node));
      this.trackLoadedPages(nodes);
    }

    prependPreviousPageProducts(html) {
      const previousGrid = html.getElementById(CollectionLoadMore.ids.productGrid);
      const currentGrid = document.getElementById(CollectionLoadMore.ids.productGrid);

      if (!previousGrid || !currentGrid) return;

      const nodes = Array.from(previousGrid.querySelectorAll(":scope > *"));
      if (!nodes.length) return;

      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      currentGrid.insertBefore(fragment, currentGrid.firstChild);

      this.trackLoadedPages(nodes);

      if (!this.prependReferenceElement || this.prependReferenceTop == null) return;

      this.deferScrollStabilization(() => {
        if (!this.prependReferenceElement || this.prependReferenceTop == null) return;

        const newTop = this.prependReferenceElement.getBoundingClientRect().top;
        const delta = newTop - this.prependReferenceTop;
        if (delta !== 0) {
          window.scrollBy({ top: delta, behavior: "auto" });
        }
        this.prependReferenceElement = null;
        this.prependReferenceTop = null;
      });
    }

    syncPaginationFromResponse(html, direction = "next") {
      const nextLoader = html.querySelector("collection-load-more");
      this.refreshFromElement(nextLoader, direction);
    }

    syncBrowserUrl(nextUrl) {
      const loadedPage = nextUrl.searchParams.get("page");
      if (!loadedPage) return;

      const currentUrl = new URL(window.location.href);
      if (loadedPage === "1") {
        currentUrl.searchParams.delete("page");
      } else {
        currentUrl.searchParams.set("page", loadedPage);
      }

      nextUrl.searchParams.forEach((value, key) => {
        if (key === "page" || key === "section_id") return;
        currentUrl.searchParams.set(key, value);
      });

      window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }

    /**
     * Hydrates component attributes/state from the server-rendered loader in the fetched markup.
     */
    refreshFromElement(templateElement, direction = "next") {
      const previousPage = CollectionLoadMore.parsePageNumber(this.dataset.currentPage);

      if (!templateElement) {
        this.dataset.nextUrl = "";
        this.dataset.prevUrl = "";
        if (this.loadMoreButton) {
          this.updateLoadMoreButtonState();
        }
        this.updatePreviousButtonState();
        return;
      }

      Array.from(templateElement.attributes).forEach((attr) => {
        if (attr.name === "id") return;

        if (attr.name === "data-prev-url") {
          const nextPrevPage = CollectionLoadMore.parsePageFromUrl(attr.value);
          const lowestLoaded = typeof this.lowestLoadedPage === "number" ? this.lowestLoadedPage : CollectionLoadMore.getCurrentPageNumber();

          if (nextPrevPage != null && nextPrevPage >= lowestLoaded) {
            return;
          }
        }

        if (attr.name === "data-next-url" && direction === "previous") {
          const incomingNextPage = CollectionLoadMore.parsePageFromUrl(attr.value);
          const highestLoaded = typeof this.highestLoadedPage === "number" ? this.highestLoadedPage : CollectionLoadMore.getCurrentPageNumber();

          if (incomingNextPage != null && incomingNextPage <= highestLoaded) {
            return;
          }
        }

        this.setAttribute(attr.name, attr.value);
      });

      this.sectionId = this.dataset.sectionId || this.sectionId;
      this.loadingText = this.dataset.loadingText || this.loadingText;
      this.finishedText = this.dataset.finishedText || "";
      this.loadMoreButtonText = this.dataset.buttonText || this.loadMoreButtonText;
      this.previousButtonText = this.dataset.previousButtonText || this.previousButtonText || "Load Previous";

      const templateButton = templateElement.querySelector("[data-load-more-button]");
      if (templateButton && this.loadMoreButton) {
        this.loadMoreButton.innerHTML = templateButton.innerHTML;
        const templateText = templateButton.textContent.trim();
        if (templateText) {
          this.loadMoreButtonText = templateText;
        }
      }

      this.setAttribute("data-button-text", this.loadMoreButtonText);
      this.setAttribute("data-previous-button-text", this.previousButtonText);
      if (this.loadMoreButton) {
        this.updateLoadMoreButtonState();
        this.ensurePreviousButtonExists();
      }
      this.updateLoadedPageBounds();
      this.updatePreviousButtonState();
      const incomingPage = CollectionLoadMore.parsePageNumber(this.dataset.currentPage);
      if (incomingPage != null && previousPage != null && incomingPage !== previousPage) {
        this.oosAppended = false;
      }
    }

    ensurePreviousButtonExists() {
      const grid = document.getElementById(CollectionLoadMore.ids.productGrid);
      if (!grid || !grid.parentElement) return;

      if (this.previousButtonWrapper && !this.previousButtonWrapper.isConnected) {
        this.previousButtonWrapper = null;
        this.prevButton = null;
      }

      if (this.prevButton && !this.prevButton.isConnected) {
        this.prevButton = null;
      }

      if (this.prevButton && this.prevButton.isConnected) {
        if (this.onPrevClick) {
          this.prevButton.removeEventListener("click", this.onPrevClick);
          this.prevButton.addEventListener("click", this.onPrevClick);
        }
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.className = CollectionLoadMore.classNames.previousButtonWrapper;
      const previousButton = document.createElement("button");
      previousButton.type = "button";
      const baseClass = this.loadMoreButton?.className || CollectionLoadMore.classNames.defaultButtonBase;
      previousButton.className = `${baseClass} ${CollectionLoadMore.classNames.previousButton}`;
      previousButton.setAttribute("data-load-previous-button", "");
      previousButton.textContent = this.previousButtonText;
      wrapper.appendChild(previousButton);

      grid.parentElement.insertBefore(wrapper, grid);
      this.previousButtonWrapper = wrapper;
      this.prevButton = previousButton;

      if (this.onPrevClick) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }
    }

    updatePreviousButtonState() {
      if (!this.prevButton) return;

      const prevUrl = this.dataset.prevUrl;
      const prevPage = CollectionLoadMore.parsePageFromUrl(prevUrl);
      const lowestLoaded = typeof this.lowestLoadedPage === "number" ? this.lowestLoadedPage : CollectionLoadMore.getCurrentPageNumber();

      const shouldEnable = Boolean(prevUrl) && (!Number.isFinite(prevPage) || prevPage < lowestLoaded);

      this.prevButton.hidden = !shouldEnable;
      this.prevButton.disabled = !shouldEnable;
      this.prevButton.textContent = this.previousButtonText;
    }

    updateLoadedPageBounds() {
      const currentPage = CollectionLoadMore.parsePageNumber(this.dataset.currentPage);
      if (currentPage == null) return;

      if (typeof this.lowestLoadedPage === "number") {
        this.lowestLoadedPage = Math.min(this.lowestLoadedPage, currentPage);
      } else {
        this.lowestLoadedPage = currentPage;
      }

      if (typeof this.highestLoadedPage === "number") {
        this.highestLoadedPage = Math.max(this.highestLoadedPage, currentPage);
      } else {
        this.highestLoadedPage = currentPage;
      }
    }

    trackLoadedPages(nodes) {
      if (!Array.isArray(nodes)) return;

      nodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        const pageValue = node.getAttribute("data-page");
        const pageNumber = CollectionLoadMore.parsePageNumber(pageValue);
        if (pageNumber == null) return;

        if (typeof this.lowestLoadedPage === "number") {
          this.lowestLoadedPage = Math.min(this.lowestLoadedPage, pageNumber);
        } else {
          this.lowestLoadedPage = pageNumber;
        }

        if (typeof this.highestLoadedPage === "number") {
          this.highestLoadedPage = Math.max(this.highestLoadedPage, pageNumber);
        } else {
          this.highestLoadedPage = pageNumber;
        }
      });
    }

    hasNextPage() {
      return Boolean(this.dataset.nextUrl);
    }

    hasPreviousPage() {
      return Boolean(this.dataset.prevUrl);
    }

    /**
     * Lazily instantiates the IntersectionObserver used for infinite scroll loading.
     */
    setupInfiniteScroll() {
      if (this.paginationType !== "infinite_scroll") return;
      if (!("IntersectionObserver" in window)) return;
      if (!this.loadMoreButton) return;
      if (this.infiniteObserver) return;

      this.boundInfiniteIntersection = this.handleInfiniteIntersection.bind(this);
      this.infiniteObserver = new IntersectionObserver(this.boundInfiniteIntersection, {
        root: null,
        rootMargin: "200px 0px",
        threshold: 0,
      });
    }

    /**
     * Keeps observed targets in sync with pagination state and pause/resume transitions.
     */
    updateInfiniteScrollObserver() {
      if (!("IntersectionObserver" in window)) return;

      if (this.dataset.paginationType !== "infinite_scroll") {
        this.teardownInfiniteScroll();
        return;
      }

      if (!this.loadMoreButton) return;

      this.setupInfiniteScroll();
      if (!this.infiniteObserver) return;

      if (this.infiniteScrollPaused) {
        try {
          this.unobserveInfiniteScrollTargets();
        } catch (pauseError) {
          console.warn("Unable to pause infinite scroll observer", pauseError);
        }
        return;
      }

      try {
        this.observeInfiniteScrollTargets();
      } catch (error) {
        console.warn("Unable to update infinite scroll observer", error);
      }
    }

    observeInfiniteScrollTargets() {
      if (!this.infiniteObserver) return;

      if (this.loadMoreButton) {
        if (this.hasNextPage()) {
          this.infiniteObserver.observe(this.loadMoreButton);
        } else {
          this.infiniteObserver.unobserve(this.loadMoreButton);
        }
      }

      if (this.prevButton) {
        if (this.hasPreviousPage()) {
          this.infiniteObserver.observe(this.prevButton);
        } else {
          this.infiniteObserver.unobserve(this.prevButton);
        }
      }
    }

    unobserveInfiniteScrollTargets() {
      if (!this.infiniteObserver) return;

      if (this.loadMoreButton) {
        this.infiniteObserver.unobserve(this.loadMoreButton);
      }

      if (this.prevButton) {
        this.infiniteObserver.unobserve(this.prevButton);
      }
    }

    /**
     * Responds to observed targets entering the viewport by loading the appropriate page.
     */
    handleInfiniteIntersection(entries) {
      if (this.dataset.paginationType !== "infinite_scroll") return;
      if (!Array.isArray(entries)) return;
      if (this.infiniteScrollPaused) return;

      entries.forEach((entry) => {
        if (!entry) return;

        if (entry.target === this.prevButton && !entry.isIntersecting) {
          if (!this.autoLoadPreviousEnabled) {
            this.autoLoadPreviousEnabled = true;
          }
          return;
        }

        if (!entry.isIntersecting) return;

        if (entry.target === this.loadMoreButton) {
          if (this.isLoading || !this.hasNextPage()) return;
          this.loadNextPage();
          return;
        }

        if (entry.target === this.prevButton) {
          if (this.isLoading || !this.autoLoadPreviousEnabled || !this.hasPreviousPage()) return;
          this.loadPreviousPage();
        }
      });
    }

    /**
     * Temporarily disables infinite scroll so restoration flows can finish without extra fetches.
     */
    pauseInfiniteScrollForRestore() {
      if (this.dataset.paginationType !== "infinite_scroll") return;
      this.infiniteScrollPaused = true;
      this.autoLoadPreviousEnabled = true;
      if (this.infiniteObserver) {
        try {
          this.unobserveInfiniteScrollTargets();
        } catch (error) {
          console.warn("Unable to unobserve infinite scroll targets while pausing", error);
        }
      }
    }

    /**
     * Re-enables infinite scroll after the user"s collection position has been restored.
     */
    resumeInfiniteScrollAfterRestore() {
      if (this.dataset.paginationType !== "infinite_scroll") return;
      if (!this.infiniteScrollPaused) return;

      this.infiniteScrollPaused = false;
      this.updateInfiniteScrollObserver();
      Promise.resolve().then(() => {
        this.autoLoadPreviousEnabled = true;
        this.observeInfiniteScrollTargets();
      });
    }

    /**
     * Disconnects the IntersectionObserver when infinite scroll is disabled or component removed.
     */
    teardownInfiniteScroll() {
      if (this.infiniteObserver) {
        try {
          this.unobserveInfiniteScrollTargets();
          this.infiniteObserver.disconnect();
        } catch (error) {
          console.warn("Unable to teardown infinite scroll observer", error);
        }
      }

      this.infiniteObserver = null;
      this.boundInfiniteIntersection = null;
    }

    /**
     * Generates a page fetch URL including current filter/sort params and section id.
     */
    buildPageRequestUrl(targetUrl) {
      if (!targetUrl) return null;

      const url = new URL(targetUrl, window.location.origin);
      const currentParams = this.getCurrentSearchParams();
      currentParams.forEach((value, key) => {
        if (key !== "page" && key !== "section_id") {
          url.searchParams.set(key, value);
        }
      });
      url.searchParams.set("section_id", this.sectionId);
      return url;
    }

    getCurrentSearchParams() {
      const rawQuery = this.dataset.currentQuery;
      if (rawQuery) {
        const normalizedQuery = rawQuery.startsWith("?") ? rawQuery.slice(1) : rawQuery;
        return new URLSearchParams(normalizedQuery);
      }

      if (typeof FacetFiltersForm !== "undefined" && FacetFiltersForm.searchParamsPrev != null) {
        return new URLSearchParams(FacetFiltersForm.searchParamsPrev);
      }

      return new URLSearchParams(window.location.search);
    }

    getCurrentQueryString() {
      return this.getCurrentSearchParams().toString();
    }

    resetOOSStateIfQueryChanged() {
      const currentQuery = this.getCurrentQueryString();
      if (currentQuery !== this.lastOosQuery) {
        this.oosAppended = false;
        this.lastOosQuery = currentQuery;
      }
    }

    /**
     * Fetches HTML for the requested URL and returns it as a parsed document.
     */
    async fetchPageDocument(url) {
      if (!url) throw new Error("Missing URL for page fetch");

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Failed to fetch page: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();
      return new DOMParser().parseFromString(text, "text/html");
    }

    /**
     * Re-initializes storefront behaviours that rely on newly injected product markup.
     */
    runPostFetchInitializers() {
      if (typeof AuroraProduct?.init === "function") {
        AuroraProduct.init();
      }

      const productGridContainer = document.getElementById("ProductGridContainer");
      if (productGridContainer) {
        facetsExecuteScripts(productGridContainer);
      }

      if (typeof FacetFiltersForm?.hideLoadingSpinners === "function") {
        FacetFiltersForm.hideLoadingSpinners();
      }
    }

    deferScrollStabilization(callback) {
      requestAnimationFrame(() => {
        requestAnimationFrame(callback);
      });
    }

    static parsePageNumber(value) {
      if (!value) return null;
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    static parsePageFromUrl(url) {
      if (!url) return null;
      try {
        const parsedUrl = new URL(url, window.location.origin);
        return this.parsePageNumber(parsedUrl.searchParams.get("page"));
      } catch (error) {
        console.warn("Unable to parse page from url", url, error);
        return null;
      }
    }

    static isCurrentNavigationBack() {
      if (!window.performance) return false;

      const [navigationEntry] = window.performance.getEntriesByType?.("navigation") || [];
      if (navigationEntry && navigationEntry.type === "back_forward") return true;

      if (window.performance.navigation && window.performance.navigation.type === window.performance.navigation.TYPE_BACK_FORWARD) {
        return true;
      }

      return false;
    }

    static shouldPauseInfiniteScrollOnConnect() {
      if (!this.isCurrentNavigationBack()) return false;

      try {
        const storedValue = sessionStorage.getItem(this.scrollStorageKey);
        if (!storedValue) return false;

        const payload = JSON.parse(storedValue);
        return payload && typeof payload === "object" && Number.isFinite(payload.page);
      } catch (error) {
        console.warn("Unable to inspect stored scroll payload for infinite scroll pause", error);
        return false;
      }
    }

    static updateHistoryToPage(page) {
      const targetPage = Number.isFinite(page) && page > 0 ? page : 1;
      const currentPage = this.getCurrentPageNumber();
      if (currentPage === targetPage) return;

      try {
        const url = new URL(window.location.href);
        if (targetPage === 1) {
          url.searchParams.delete("page");
        } else {
          url.searchParams.set("page", String(targetPage));
        }

        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      } catch (error) {
        console.warn("Unable to update history page parameter", error);
      }
    }

    static pauseInfiniteScrollForRestore() {
      document.querySelectorAll("collection-load-more").forEach((element) => {
        element?.pauseInfiniteScrollForRestore?.();
      });
    }

    static resumeInfiniteScrollAfterRestore() {
      document.querySelectorAll("collection-load-more").forEach((element) => {
        element?.resumeInfiniteScrollAfterRestore?.();
      });
    }

    static registerGlobalEventsOnce() {
      if (this.globalEventsRegistered) return;

      this.boundRecordProductClick = this.recordProductClick.bind(this);
      this.boundLogStoredScrollState = this.logStoredScrollState.bind(this);

      document.addEventListener("click", this.boundRecordProductClick, true);
      window.addEventListener("pageshow", this.boundLogStoredScrollState);

      this.globalEventsRegistered = true;
    }

    static recordProductClick(event) {
      const anchor = event.target.closest("a[href]");
      if (!anchor) return;

      const productElement = anchor.closest("[data-product-id]");
      if (!productElement) return;

      const productId = productElement.getAttribute("data-product-id");
      if (!productId) return;

      const gridElement = anchor.closest(".collection__products-grid-item");
      if (!gridElement) return;

      const page = gridElement.getAttribute("data-page");

      const payload = {
        productId,
        scrollY: window.scrollY || window.pageYOffset || 0,
        page: page ? parseInt(page, 10) : 1,
      };

      try {
        sessionStorage.setItem(this.scrollStorageKey, JSON.stringify(payload));
      } catch (storageError) {
        console.warn("Unable to persist collection scroll state", storageError);
        return;
      }

      console.log("Collection scroll state saved", payload);

      const targetPage = Number.isFinite(payload.page) && payload.page > 0 ? payload.page : 1;
      this.updateHistoryToPage(targetPage);
    }

    static logStoredScrollState(event) {
      if (!this.isBackNavigation(event)) return;

      if ("scrollRestoration" in history) {
        history.scrollRestoration = "manual";
      }
      try {
        this.pauseInfiniteScrollForRestore();
        const resumeInfiniteScroll = () => this.resumeInfiniteScrollAfterRestore();

        const storedValue = sessionStorage.getItem(this.scrollStorageKey);
        if (!storedValue) {
          resumeInfiniteScroll();
          return;
        }

        const payload = JSON.parse(storedValue);
        if (!payload || typeof payload !== "object") {
          resumeInfiniteScroll();
          return;
        }

        const currentPage = this.getCurrentPageNumber();
        console.log("Current page:", currentPage, "Payload page:", payload.page, currentPage === payload.page);

        if (currentPage > 1 && payload.productId) {
          const productSelector = `[data-product-id="${payload.productId}"]`;
          const productElement = document.querySelector(productSelector);

          if (productElement) {
            window.requestAnimationFrame(() => {
              const { top, height } = productElement.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              // Calculate position to center the element
              const elementCenter = top + height / 2;
              const targetPosition = Math.max(0, window.scrollY + elementCenter - viewportHeight / 2);
              window.scrollTo({ top: targetPosition, behavior: "auto" });
              console.log("Collection scroll state restored via product", payload);
              resumeInfiniteScroll();
            });
            return;
          }
        }

        if (typeof payload.scrollY === "number") {
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: Math.max(0, payload.scrollY), behavior: "auto" });
            console.log("Collection scroll state restored via scroll position", payload);
            resumeInfiniteScroll();
          });
          return;
        }

        resumeInfiniteScroll();
      } catch (parseError) {
        console.warn("Unable to read collection scroll state", parseError);
        this.resumeInfiniteScrollAfterRestore();
      }
    }

    static isBackNavigation(event) {
      if (event?.persisted) return true;

      if (!window.performance) return false;

      const [navigationEntry] = window.performance.getEntriesByType?.("navigation") || [];
      if (navigationEntry && navigationEntry.type === "back_forward") return true;

      if (window.performance.navigation && window.performance.navigation.type === window.performance.navigation.TYPE_BACK_FORWARD) {
        return true;
      }

      return false;
    }

    static get scrollStorageKey() {
      return "collection:last-product-scroll";
    }

    static getCurrentPageNumber() {
      const { search } = new URL(window.location.href);
      const pageParam = new URLSearchParams(search).get("page");
      const parsedPage = parseInt(pageParam, 10);
      return Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
    }

    static get selectors() {
      return {
        loadMoreButton: "[data-load-more-button]",
        spinner: ".collection-load-more__spinner",
      };
    }

    static get ids() {
      return {
        productGrid: "product-grid",
      };
    }

    static get classNames() {
      return {
        previousButtonWrapper: "collection-load-more__previous-wrapper",
        previousButton: "collection-load-more__previous-button",
        defaultButtonBase: "button button--primary",
      };
    }
  }

  customElements.define("collection-load-more", CollectionLoadMore);
}
